import {
  AlertTriangle,
  ArrowDownRight,
  ArrowRight,
  ArrowUpRight,
  Building2,
  CheckCircle2,
  Timer,
  TrendingUp,
  Users,
  Wallet
} from "lucide-react";

import { Link } from "react-router-dom";
import ComponentErrorBoundary from "@components/ui-components/error-boundary";
import StatusChip from "@components/ui-components/status-chip";
import DashboardHeader from "./dashboard-header";
import StatusCard from "./status-card";
import classnames from "classnames";
import { formatCurrency } from "@src/utility/helpers/utils";
import {
  DEBIT_NOTE_STATUS_LABEL,
  DEBIT_NOTE_STATUS_TONE
} from "@src/utility/constants/panel/common";

const toneDot = {
  destructive: "bg-danger",
  warning    : "bg-warning",
  info       : "bg-info"
};

// Only `kyc_name_mismatch` ships today; anything the API adds later falls back to a
// warning dot pointing at the brands list rather than disappearing off the board.
const PENDING_ACTION_META = {
  kyc_name_mismatch: { tone: "destructive", to: "/publishers" }
};
const DEFAULT_PENDING_ACTION_META = { tone: "warning", to: "/brands" };

const EmptyRow = ({ children }) => (
  <div className="py-6 text-center text-sm text-muted">{children}</div>
);

// The full board: everything here hangs off debit notes, custody and
// disbursements, which only exist when Nook Pay is the one paying out.
const ManagedPayoutDashboard = ({
  stats,
  pendingActions,
  activeDebitNotes,
  generatedAt,
  refreshing,
  refresh
}) => {
  const custody = stats.custody_balance || [];
  const secondaryStats = [
    {
      label: "Total brands",
      value: stats.total_brands ?? 0,
      icon : Building2,
      to   : "/brands"
    },
    {
      label: "Active publishers",
      value: stats.active_publishers ?? 0,
      icon : Users,
      to   : "/publishers"
    },
    {
      label: "Pending actions",
      value: stats.pending_actions ?? 0,
      icon : AlertTriangle,
      to   : "/debit-notes"
    },
    {
      label: "This-month payout volume",
      value: formatCurrency(stats.month_payout_volume),
      icon : TrendingUp,
      to   : "/disbursements"
    }
  ];

  return (
    <div className="space-y-6 p-6">
      <DashboardHeader
        title="Ops control room"
        subtitle="Live view of debit notes, funds movement and SLA breaches across all brands."
        generatedAt={generatedAt}
        refreshing={refreshing}
        refresh={refresh} />

      {/* Live status board */}
      <ComponentErrorBoundary name="live status board" className="md:col-span-4">
      <div className="grid gap-3 md:grid-cols-4">
        <StatusCard to="/debit-notes">
          <div className="flex items-center justify-between text-xs text-muted">
            <span className="font-medium uppercase tracking-wider">
              Awaiting brand pay
            </span>
            <Timer className="h-4 w-4" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-3xl font-semibold">
              {stats.awaiting_brand_pay ?? 0}
            </div>
            <span className="text-xs text-muted">debit notes</span>
          </div>
          <span className="mt-2 inline-flex items-center gap-1 text-xs text-accent group-hover:underline">
            Review queue <ArrowRight className="h-3 w-3" />
          </span>
        </StatusCard>

        <StatusCard to="/disbursements">
          <div className="flex items-center justify-between text-xs text-muted">
            <span className="font-medium uppercase tracking-wider">
              Ready to disburse
            </span>
            <Wallet className="h-4 w-4 text-success" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-3xl font-semibold text-success">
              {stats.ready_to_disburse ?? 0}
            </div>
            <span className="text-xs text-muted">payouts</span>
          </div>
          <span className="mt-2 inline-flex items-center gap-1 text-xs text-accent group-hover:underline">
            Pay now <ArrowRight className="h-3 w-3" />
          </span>
        </StatusCard>

        <StatusCard to="/publishers">
          <div className="flex items-center justify-between text-xs text-muted">
            <span className="font-medium uppercase tracking-wider">
              SLA breach risk
            </span>
            <AlertTriangle className="h-4 w-4 text-danger" />
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-3xl font-semibold text-danger">
              {stats.sla_breach_risk ?? 0}
            </div>
            <span className="text-xs text-muted">
              KYC ≤ {stats.sla_breach_within_days ?? 0}d
            </span>
          </div>
          <div className="mt-2 text-xs text-accent group-hover:underline">
            View publishers →
          </div>
        </StatusCard>

        <StatusCard to="/debit-notes">
          <div className="flex items-center justify-between text-xs text-muted">
            <span className="font-medium uppercase tracking-wider">
              Custody balance
            </span>
            <CheckCircle2 className="h-4 w-4" />
          </div>
          <div className="mt-2 space-y-2.5">
            {custody.length === 0 && (
              <div className="text-xs text-muted">No funds in custody</div>
            )}
            {custody.map((c) => (
              <div key={c.currency}>
                <div className="flex items-center justify-between text-[11px]">
                  <span className="font-semibold text-gray-900">
                    {c.currency}
                  </span>
                  <span className="text-muted">
                    {c.in > 0 ? `${c.held_pct}% held` : "—"}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-2 text-[11px]">
                  <span className="flex items-center gap-1 text-success">
                    <ArrowDownRight className="h-3 w-3" />
                    In {formatCurrency(c.in, c.currency)}
                  </span>
                  <span className="flex items-center gap-1 text-muted">
                    <ArrowUpRight className="h-3 w-3" />
                    Out {formatCurrency(c.out, c.currency)}
                  </span>
                </div>
                <div className="mt-1 h-1 w-full overflow-hidden rounded-full bg-gray-100">
                  <div
                    className="h-full bg-navy"
                    style={{ width: `${Math.min(c.held_pct || 0, 100)}%` }}
                  />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-2 text-[10px] text-muted">
            Tracked per currency · no FX conversion
          </div>
        </StatusCard>
      </div>
      </ComponentErrorBoundary>

      {/* Secondary stat strip */}
      <ComponentErrorBoundary name="stat strip" className="md:col-span-4">
      <div className="grid gap-3 md:grid-cols-4">
        {secondaryStats.map((s) => (
          <StatusCard key={s.label} to={s.to}>
            <div className="flex items-center justify-between">
              <div className="text-sm text-muted">{s.label}</div>
              <s.icon className="h-4 w-4 text-muted" />
            </div>
            <div className="mt-2 text-2xl font-semibold">{s.value}</div>
          </StatusCard>
        ))}
      </div>
      </ComponentErrorBoundary>

      <ComponentErrorBoundary name="pending actions and debit notes" className="lg:col-span-3">
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm lg:col-span-2">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-semibold">Pending actions</h2>
            <span className="text-xs text-muted">
              Prioritised · click to open
            </span>
          </div>
          {pendingActions.length === 0 ? (
            <EmptyRow>Nothing needs your attention right now.</EmptyRow>
          ) : (
            <ul className="divide-y divide-gray-100">
              {pendingActions.map((a) => {
                const meta = PENDING_ACTION_META[a.type] || DEFAULT_PENDING_ACTION_META;
                return (
                  <li key={a._id}>
                    <Link
                      to={meta.to}
                      className="flex items-center justify-between py-3 text-sm hover:bg-gray-50"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          className={classnames(
                            "h-2 w-2 shrink-0 rounded-full",
                            toneDot[meta.tone]
                          )}
                        />
                        <span>
                          {a.title}
                          {a.account_name && (
                            <span className="text-muted"> · {a.account_name}</span>
                          )}
                        </span>
                      </div>
                      <ArrowRight className="h-4 w-4 shrink-0 text-muted" />
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm">
          <h2 className="mb-3 font-semibold">Active debit notes</h2>
          {activeDebitNotes.length === 0 ? (
            <EmptyRow>No active debit notes.</EmptyRow>
          ) : (
            <ul className="space-y-3">
              {activeDebitNotes.map((d) => (
                <li key={d._id} className="rounded-md border border-gray-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium">
                        {d.debit_note_number}
                      </div>
                      <div className="text-xs text-muted">
                        {d.account_name || "—"} · {d.payout_period}
                      </div>
                    </div>
                    <StatusChip
                      tone={DEBIT_NOTE_STATUS_TONE[d.status] || "neutral"}
                      label={DEBIT_NOTE_STATUS_LABEL[d.status] || d.status}
                    />
                  </div>
                  <div className="mt-2 text-sm font-semibold">
                    {formatCurrency(d.amount, d.currency)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
      </ComponentErrorBoundary>
    </div>
  );
};

export default ManagedPayoutDashboard;
